import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  FileText, 
  Download, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Hash,
  Building,
  Truck,
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  ArrowLeft,
  Package,
  Loader2,
  BarChart3
} from 'lucide-react';
import { InBooks24Logo } from './InBooks24Logo';
import { toast } from 'sonner';
import { purchaseOrderApiService, PoHeader, PoItemDetail, LastTransaction, RawItemTransactionsResponse } from "../services/purchaseOrderApi";
import { rawItemApiService, RawItem } from "../services/rawItemApi";
import { branchApiService, Branch } from "../services/branchApi";
import { grnApiService, GRNConsumptionDetail, RawItemConsumptionResponse } from "../services/grnApi";
import { useAuth } from "../contexts/AuthContext";
import { useBranch } from "../contexts/BranchContext";
import { userApiService } from "../services/userApi";
import { User } from "../types/user";
import { PermissionGate } from "./ui/PermissionGate";
import { getCurrencyConfig } from "../config/uiConfig";

// Mock purchase order data for fallback
const mockPurchaseOrder = {
  poNumber: 'PO-2024-001',
  purchaseDate: '2024-01-15',
  poType: 'Standard Purchase Order',
  effectiveStartDate: '2024-01-20',
  effectiveEndDate: '2024-03-20',
  status: 'APPROVED',
  shipTo: 'Branch not specified',
  vendorCodeNumber: 'VND-001',
  vendorCodeDate: '2024-01-10',
  supplier: 'ABC Steel Industries',
  supplierDetails: {
    name: 'ABC Steel Industries',
    address: '123 Industrial Area, Phase 2\nMumbai - 400001, Maharashtra',
    phone: '+91-98765-43210',
    email: 'purchase@abcsteel.com',
    gstin: '27AABCS1234D1Z5',
    code: 'ASI001'
  },
  companyDetails: {
    name: '',
    address: '',
    phone: '',
    email: '',
    gstin: ''
  },
  modeOfDispatch: 'Road Transport',
  transportationCharges: 2500,
  insuranceCharges: 1200,
  packingCharges: 800,
  installationServiceCharges: 5000,
  projectLeadTime: '15 days',
  otherCharges: 500,
  termConditions: 'Payment terms: Net 30 days from delivery date. All goods must be delivered in original packaging. Quality inspection will be conducted upon receipt. Any defective items must be replaced within 7 days.',
  paymentTerms: 'Net 30',
  lineItems: [
    {
      id: '1',
      category: 'Raw Materials',
      itemName: 'Steel Rod 12mm',
      size: '12mm',
      purchaseRate: 65.50,
      quantity: 1000,
      hsn: '72142000',
      unit: 'Kg',
      grade: 'A',
      weight: 1.2,
      igst: 18,
      cgst: 0,
      sgst: 0,
      totalWeight: 1200,
      totalTax: 11790,
      amount: 65500
    },
    {
      id: '2',
      category: 'Raw Materials',
      itemName: 'Aluminum Sheet',
      size: '2mm x 1m x 1m',
      purchaseRate: 145.00,
      quantity: 500,
      hsn: '76061200',
      unit: 'Kg',
      grade: 'B',
      weight: 2.5,
      igst: 18,
      cgst: 0,
      sgst: 0,
      totalWeight: 1250,
      totalTax: 13050,
      amount: 72500
    },
    {
      id: '3',
      category: 'Components',
      itemName: 'Electronic Components',
      size: 'Standard',
      purchaseRate: 850.00,
      quantity: 200,
      hsn: '85423100',
      unit: 'Pieces',
      grade: 'Premium',
      weight: 0.5,
      igst: 18,
      cgst: 0,
      sgst: 0,
      totalWeight: 100,
      totalTax: 30600,
      amount: 170000
    }
  ],
  grandTotalTax: 55440,
  totalAmount: 308000,
  grandTotal: 373440,
  createdBy: 'Rajesh Kumar',
  approvedBy: 'Priya Sharma',
  createdDate: '2024-01-15',
  approvedDate: '2024-01-16'
};

export function PurchaseOrderPdfView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch: branchFromContext } = useBranch();
  const [purchaseOrder, setPurchaseOrder] = useState<PoHeader | null>(null);
  const [lineItems, setLineItems] = useState<PoItemDetail[]>([]);
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [branchInfo, setBranchInfo] = useState<Branch | null>(null);
  const [approvedByUser, setApprovedByUser] = useState<User | null>(null);
  const [signedByUser, setSignedByUser] = useState<User | null>(null);
  const [contactPersonUser, setContactPersonUser] = useState<User | null>(null);
  const [shipToBranch, setShipToBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPO, setCurrentPO] = useState(mockPurchaseOrder);
  const [staticTerms, setStaticTerms] = useState<string | string[]>('');
  
  // Analysis dialog state
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState<Array<{
    itemName: string;
    category: string;
    size: string;
    poRate: number;
    inventoryQty: number;
    pastTransactions: LastTransaction[];
  }>>([]);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPurchaseOrder();
    }
  }, [id]);

  // Set document title to PO number when printing
  useEffect(() => {
    const handleBeforePrint = () => {
      const poNumber = currentPO.poNumber || purchaseOrder?.poNumber || 'PO';
      document.title = poNumber;
    };

    const handleAfterPrint = () => {
      // Optionally restore original title, but keep PO number for now
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [currentPO.poNumber, purchaseOrder?.poNumber]);

  const fetchPurchaseOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [poData, itemsData, rawItemsData, branchesData] = await Promise.all([
        purchaseOrderApiService.getPurchaseOrderById(id!),
        purchaseOrderApiService.getPoItemDetails(id!),
        rawItemApiService.getRawItems(),
        branchApiService.getBranches()
      ]);
      
      setPurchaseOrder(poData);
      setLineItems(itemsData);
      
      // Handle different response formats for raw items
      let rawItemsArray: RawItem[] = [];
      if (rawItemsData && Array.isArray(rawItemsData)) {
        rawItemsArray = rawItemsData;
      } else if (rawItemsData && typeof rawItemsData === 'object' && 'data' in rawItemsData) {
        rawItemsArray = (rawItemsData as any).data || [];
      } else if (rawItemsData && typeof rawItemsData === 'object' && 'success' in rawItemsData && 'data' in rawItemsData) {
        rawItemsArray = (rawItemsData as any).data || [];
      }
      setRawItems(rawItemsArray);
      
      // Handle different response formats for branches
      let branchesArray: Branch[] = [];
      if (branchesData && Array.isArray(branchesData)) {
        branchesArray = branchesData;
      } else if (branchesData && typeof branchesData === 'object' && 'data' in branchesData) {
        branchesArray = (branchesData as any).data || [];
      } else if (branchesData && typeof branchesData === 'object' && 'success' in branchesData && 'data' in branchesData) {
        branchesArray = (branchesData as any).data || [];
      }
      // console.log('Branches data:', branchesArray);
      

      // Fetch approved user information if PO is approved
      if (poData.status === 'APPROVED' && poData.approvedBy) {
        try {
          // console.log('Fetching approved user with ID:', poData.approvedBy);
          const approvedUserResponse = await userApiService.getUserById(poData.approvedBy.toString());
          // console.log('Approved user response:', approvedUserResponse);
          if (approvedUserResponse.success && approvedUserResponse.data) {
            // console.log('Setting approved user:', approvedUserResponse.data);
            setApprovedByUser(approvedUserResponse.data);
          } else {
            // console.warn('Approved user response not successful or no data:', approvedUserResponse);
          }
        } catch (error) {
          // console.error('Error fetching approved user:', error);
          // Continue without approved user info
        }
      }

      // Fetch signed by user information if PO is approved or closed and has signedBy
      if ((poData.status === 'APPROVED' || poData.status === 'CLOSED') && poData.signedBy) {
        try {
          // console.log('Fetching signed by user with ID:', poData.signedBy);
          const signedByUserResponse = await userApiService.getUserById(poData.signedBy.toString());
          // console.log('Signed by user response:', signedByUserResponse);
          if (signedByUserResponse.success && signedByUserResponse.data) {
            // console.log('Setting signed by user:', signedByUserResponse.data);
            setSignedByUser(signedByUserResponse.data);
          } else {
            // console.warn('Signed by user response not successful or no data:', signedByUserResponse);
          }
        } catch (error) {
          // console.error('Error fetching signed by user:', error);
          // Continue without signed by user info
        }
      }

      // Fetch static terms
      try {
        const terms = await purchaseOrderApiService.getStaticTerms();
        setStaticTerms(terms);
      } catch (error) {
        // console.error('Error fetching static terms:', error);
        // Continue without static terms
      }
      
      // Find the branch information for the PO (company branch)
      let foundBranchInfo = null;
      if (poData.branchId && branchesArray.length > 0) {
        foundBranchInfo = branchesArray.find(b => b.id === poData.branchId);
        // console.log('Found branch:', foundBranchInfo, 'for branchId:', poData.branchId);
        setBranchInfo(foundBranchInfo || null);
        
        // Fetch contact person user details if contactPerson is a user ID
        if (foundBranchInfo?.contactPerson) {
          try {
            const contactPersonId = foundBranchInfo.contactPerson.toString();
            // Check if it's a numeric ID (not a name)
            if (!isNaN(Number(contactPersonId))) {
              // console.log('Fetching contact person user with ID:', contactPersonId);
              const contactPersonResponse = await userApiService.getUserById(contactPersonId);
              // console.log('Contact person user response:', contactPersonResponse);
              if (contactPersonResponse.success && contactPersonResponse.data) {
                // console.log('Setting contact person user:', contactPersonResponse.data);
                setContactPersonUser(contactPersonResponse.data);
              }
            }
          } catch (error) {
            // console.error('Error fetching contact person user:', error);
            // Continue without contact person user info
          }
        }
      } else {
        // console.log('No branch data found. branchId:', poData.branchId, 'branchesArray:', branchesArray);
      }

      // Find the ship-to branch information
      let foundShipToBranch = null;
      if (poData.shipToBranchId && branchesArray.length > 0) {
        foundShipToBranch = branchesArray.find(b => b.id === poData.shipToBranchId);
        // console.log('Found ship-to branch:', foundShipToBranch, 'for shipToBranchId:', poData.shipToBranchId);
        setShipToBranch(foundShipToBranch || null);
      } else {
        // console.log('No ship-to branch data found. shipToBranchId:', poData.shipToBranchId, 'branchesArray:', branchesArray);
      }

      // Transform API data to match the new component structure
      const transformedPO = {
        poNumber: poData.poNumber || '',
        purchaseDate: poData.purchaseDate || '',
        poType: poData.poType || '',
        effectiveStartDate: poData.effectiveStartDate || '',
        effectiveEndDate: poData.effectiveEndDate || '',
        status: poData.status || '',
        shipTo: foundShipToBranch?.branchName || '',
        vendorCodeNumber: poData.vendorQuoteNo || '',
        vendorCodeDate: poData.vendorQuoteDate || '',
        supplier: poData.supplier?.supplierName || '',
        supplierDetails: {
          name: poData.supplier?.supplierName || '',
          address: poData.supplier?.billAddress || '',
          phone: poData.supplier?.contactNo || '',
          email: poData.supplier?.email || '',
          gstin: poData.supplier?.gstNo || '',
          code: poData.supplier?.supplierCode || ''
        },
        companyDetails: {
          name: foundBranchInfo?.branchName || '',
          address: foundBranchInfo?.address || '',
          phone: foundBranchInfo?.phone || '',
          email: foundBranchInfo?.email || '',
          gstin: foundBranchInfo?.gstin || ''
        },
        modeOfDispatch: poData.modeOfDespatch || '',
        transportationCharges: poData.transportationCharges,
        insuranceCharges: poData.insuranceCharges,
        packingCharges: poData.packingCharges,
        installationServiceCharges: poData.installAndServiceCharges,
        projectLeadTime: poData.projectLeadTime || '',
        otherCharges: poData.itemCostOthCharges,
        termConditions: poData.deliveryTerms || '',
        paymentTerms: poData.paymentTerms || '',
        lineItems: itemsData.map((item, index) => {
          // Find the corresponding raw item to get the correct unit
          let rawItem;
          if (item.rawItem) {
            // Real server format - item has nested rawItem object
            rawItem = item.rawItem;
          } else if (item.rawItemId) {
            // Mock server format - item has rawItemId field
            rawItem = rawItemsArray.find(ri => ri.id.toString() === item.rawItemId.toString());
          }
          
          return {
            id: item.id.toString(),
            category: rawItem?.itemCategory?.categoryName || '',
            itemName: rawItem?.itemName || item.itemDescription || '',
            size: rawItem?.size || '',
            purchaseRate: item.unitPrice,
            quantity: item.quantity,
            hsn: rawItem?.hsn?.hsnCode || '',
            unit: rawItem?.itemUnit || item.unitOfMeasure || '',
            grade: rawItem?.grade || '',
            weight: rawItem?.weight || 0,
            igst: rawItem?.hsn?.currentIgstRate || 0,
            cgst: rawItem?.hsn?.currentCgstRate || 0,
            sgst: rawItem?.hsn?.currentSgstRate || 0,
            totalWeight: rawItem?.weight && item.quantity ? rawItem.weight * item.quantity : 0,
            totalTax: item.taxAmount,
            amount: item.totalAmount
          };
        }),
        grandTotalTax: poData.grandTotalTax,
        totalAmount: poData.totalAmount,
        grandTotal: poData.grandTotal,
        createdBy: poData.createdBy?.toString() || '',
        approvedBy: poData.approvedBy?.toString() || '',
        createdDate: poData.purchaseDate || '',
        approvedDate: poData.approvedDate || ''
      };
      
      setCurrentPO(transformedPO);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch purchase order';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/purchase-orders');
  };

  // Get currency symbol helper
  const getCurrencySymbol = () => {
    const currency = purchaseOrder?.currency || 'INR';
    const config = getCurrencyConfig(currency);
    return config.symbol;
  };

  // Get currency name for amount in words
  const getCurrencyName = () => {
    const currency = purchaseOrder?.currency || 'INR';
    const config = getCurrencyConfig(currency);
    return config.names;
  };

  // Convert number to words
  const numberToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertHundreds = (n: number): string => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    };

    if (num === 0) return 'Zero';
    
    const currency = purchaseOrder?.currency || 'INR';
    const config = getCurrencyConfig(currency);
    const isINR = config.useIndianNumbering;
    
    // For INR, use Indian numbering system (Lakhs, Crores)
    if (isINR) {
      let result = '';
      const numStr = Math.floor(num).toString();
      const decimalPart = Math.round((num % 1) * 100);
      
      // Handle Crores
      if (numStr.length > 7) {
        const crores = parseInt(numStr.slice(0, numStr.length - 7));
        if (crores > 0) {
          result += convertHundreds(crores) + ' Crore ';
        }
      }
      
      // Handle Lakhs
      if (numStr.length > 5) {
        const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5));
        if (lakhs > 0) {
          result += convertHundreds(lakhs) + ' Lakh ';
        }
      }
      
      // Handle Thousands
      if (numStr.length > 3) {
        const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3));
        if (thousands > 0) {
          result += convertHundreds(thousands) + ' Thousand ';
        }
      }
      
      // Handle Hundreds
      const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)));
      if (hundreds > 0) {
        result += convertHundreds(hundreds);
      }
      
      const currencyNames = getCurrencyName();
      result = result.trim() + ' ' + currencyNames.major;
      
      // Add paise if decimal part exists
      if (decimalPart > 0) {
        result += ' and ' + convertHundreds(decimalPart) + ' ' + currencyNames.minor;
      }
      
      return result.trim();
    } else {
      // For other currencies, use standard numbering system
      let result = '';
      const numStr = Math.floor(num).toString();
      const decimalPart = Math.round((num % 1) * 100);
      
      // Handle Millions
      if (numStr.length > 6) {
        const millions = parseInt(numStr.slice(0, numStr.length - 6));
        if (millions > 0) {
          result += convertHundreds(millions) + ' Million ';
        }
      }
      
      // Handle Thousands
      if (numStr.length > 3) {
        const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 6), numStr.length - 3));
        if (thousands > 0) {
          result += convertHundreds(thousands) + ' Thousand ';
        }
      }
      
      // Handle Hundreds
      const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)));
      if (hundreds > 0) {
        result += convertHundreds(hundreds);
      }
      
      const currencyNames = getCurrencyName();
      result = result.trim() + ' ' + currencyNames.major;
      
      // Add cents if decimal part exists
      if (decimalPart > 0) {
        result += ' and ' + convertHundreds(decimalPart) + ' ' + currencyNames.minor;
      }
      
      return result.trim();
    }
  };

  const formatCurrency = (amount: number) => {
    // Get currency from purchase order, default to INR if not available
    const currency = purchaseOrder?.currency || 'INR';
    const config = getCurrencyConfig(currency);
    const symbol = config.symbol;
    
    // Format the number with locale-specific formatting
    const formattedNumber = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    
    // Explicitly prepend the currency symbol to ensure it shows in print
    return `${symbol}${formattedNumber}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // GST helpers
  const extractGstStateCode = (gstin?: string) => {
    if (!gstin || typeof gstin !== 'string' || gstin.length < 2) return null;
    const code = gstin.slice(0, 2);
    return code;
  };

  const isInterStateSupply = () => {
    const companyState = extractGstStateCode(currentPO?.companyDetails?.gstin);
    const supplierState = extractGstStateCode(currentPO?.supplierDetails?.gstin);
    if (!companyState || !supplierState) return false; // default to intra-state if unknown
    return companyState !== supplierState;
  };

  // Check if all items have NA for IGST, CGST, or SGST
  const shouldHideTaxColumns = useMemo(() => {
    if (!currentPO.lineItems || currentPO.lineItems.length === 0) {
      return { hideIGST: false, hideCGST: false, hideSGST: false };
    }

    let allIGSTNA = true;
    let allCGSTNA = true;
    let allSGSTNA = true;

    currentPO.lineItems.forEach((item) => {
      const taxPercent = (item as any).igst ?? (item as any).taxPercent ?? (item as any).gstPercent ?? 0;
      const interstate = isInterStateSupply();
      const igstPct = interstate ? taxPercent : 0;
      const cgstPct = interstate ? 0 : taxPercent / 2;
      const sgstPct = interstate ? 0 : taxPercent / 2;

      if (igstPct > 0) allIGSTNA = false;
      if (cgstPct > 0) allCGSTNA = false;
      if (sgstPct > 0) allSGSTNA = false;
    });

    return {
      hideIGST: allIGSTNA,
      hideCGST: allCGSTNA,
      hideSGST: allSGSTNA
    };
  }, [currentPO.lineItems]);

  const isApproved = (status: string) => {
    return status?.toUpperCase() === 'APPROVED';
  };

  const isApprovedOrClosed = (status: string) => {
    const upperStatus = status?.toUpperCase();
    return upperStatus === 'APPROVED' || upperStatus === 'CLOSED';
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft': return <FileText className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <AlertCircle className="h-4 w-4" />;
      default: return <CheckCircle className="h-4 w-4" />;
    }
  };

  const handleDownloadPDF = () => {
    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to download PDF');
      return;
    }

    // Get the current page content
    const pdfContent = document.querySelector('.purchase-order-pdf')?.innerHTML;
    if (!pdfContent) {
      toast.error('Unable to generate PDF content');
      return;
    }

    // Create HTML for the print window
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Purchase Order ${purchaseOrder?.poNumber || ''}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 0.15in;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 0;
              color: #333;
              line-height: 1.4;
            }
            .purchase-order-pdf {
              max-width: 100%;
              margin: 0 auto;
            }
            .pdf-content {
              background: white;
              padding: 0;
            }
            @media print {
              .pdf-content {
                padding: 0 !important;
              }
              .purchase-order-pdf {
                padding: 0 !important;
              }
              .signature-block {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .signature-block * {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
            }
            .card {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              margin-bottom: 16px;
              overflow: hidden;
            }
            .card-header {
              background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
              padding: 16px;
              border-bottom: 1px solid #e5e7eb;
            }
            .card-content {
              padding: 16px;
            }
            .card-title {
              font-size: 18px;
              font-weight: 600;
              margin: 0;
              color: #1f2937;
            }
            .grid {
              display: grid;
              gap: 16px;
            }
            .grid-cols-2 {
              grid-template-columns: repeat(2, 1fr);
            }
            .grid-cols-3 {
              grid-template-columns: repeat(3, 1fr);
            }
            .grid-cols-4 {
              grid-template-columns: repeat(4, 1fr);
            }
            .space-y-2 > * + * {
              margin-top: 8px;
            }
            .space-y-4 > * + * {
              margin-top: 16px;
            }
            .text-sm {
              font-size: 14px;
            }
            .text-xs {
              font-size: 12px;
            }
            .font-semibold {
              font-weight: 600;
            }
            .font-medium {
              font-weight: 500;
            }
            .text-gray-600 {
              color: #6b7280;
            }
            .text-gray-700 {
              color: #374151;
            }
            .text-gray-800 {
              color: #1f2937;
            }
            .text-gray-900 {
              color: #111827;
            }
            .bg-gray-50 {
              background-color: #f9fafb;
            }
            .bg-gray-100 {
              background-color: #f3f4f6;
            }
            .border {
              border: 1px solid #e5e7eb;
            }
            .border-gray-200 {
              border-color: #e5e7eb;
            }
            .rounded {
              border-radius: 4px;
            }
            .rounded-lg {
              border-radius: 8px;
            }
            .p-4 {
              padding: 16px;
            }
            .p-6 {
              padding: 24px;
            }
            .px-4 {
              padding-left: 16px;
              padding-right: 16px;
            }
            .py-2 {
              padding-top: 8px;
              padding-bottom: 8px;
            }
            .py-3 {
              padding-top: 12px;
              padding-bottom: 12px;
            }
            .mb-4 {
              margin-bottom: 16px;
            }
            .mb-6 {
              margin-bottom: 24px;
            }
            .mt-4 {
              margin-top: 16px;
            }
            .mt-6 {
              margin-top: 24px;
            }
            .flex {
              display: flex;
            }
            .items-center {
              align-items: center;
            }
            .justify-between {
              justify-content: space-between;
            }
            .gap-2 {
              gap: 8px;
            }
            .gap-4 {
              gap: 16px;
            }
            .w-full {
              width: 100%;
            }
            .h-8 {
              height: 32px;
            }
            .h-12 {
              height: 48px;
            }
            .table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            .table th,
            .table td {
              border: 1px solid #e5e7eb;
              padding: 4px 6px;
              text-align: left;
              font-size: 10px;
            }
            .table th {
              background-color: #f9fafb;
              font-weight: 600;
              font-size: 10px;
              color: #374151;
            }
            .table td {
              font-size: 10px;
            }
            .badge {
              display: inline-flex;
              align-items: center;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              font-weight: 500;
            }
            .badge.bg-green-100 {
              background-color: #dcfce7;
              color: #166534;
            }
            .badge.bg-yellow-100 {
              background-color: #fef3c7;
              color: #92400e;
            }
            .badge.bg-red-100 {
              background-color: #fee2e2;
              color: #991b1b;
            }
            .badge.bg-gray-100 {
              background-color: #f3f4f6;
              color: #374151;
            }
            .separator {
              height: 1px;
              background-color: #e5e7eb;
              margin: 16px 0;
            }
            .text-center {
              text-align: center;
            }
            .text-right {
              text-align: right;
            }
            .hidden {
              display: none !important;
            }
            @media print {
              .no-print {
                display: none !important;
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                margin: 0;
                padding: 0;
              }
              .purchase-order-pdf {
                margin: 0;
                padding: 0;
                max-width: 100%;
              }
              .pdf-content {
                padding: 8px !important;
              }
              .table {
                font-size: 8px !important;
                width: 100% !important;
                table-layout: fixed !important;
                word-wrap: break-word;
                page-break-inside: auto !important;
                max-width: 100% !important;
              }
              .table-wrapper {
                width: 100% !important;
                overflow: visible !important;
              }
              .table th,
              .table td {
                padding: 2px 3px !important;
                font-size: 8px !important;
                line-height: 1.2 !important;
                word-wrap: break-word !important;
                word-break: break-word !important;
                overflow-wrap: break-word !important;
                vertical-align: top !important;
                white-space: normal !important;
              }
              .table th {
                font-size: 8px !important;
                padding: 2px 3px !important;
                white-space: normal !important;
              }
              .table td {
                font-size: 8px !important;
                word-break: normal !important;
                overflow-wrap: break-word !important;
                white-space: normal !important;
              }
              .table tr {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
              }
              .table th.item-description,
              .table td.item-description {
                width: 300px !important;
                max-width: 300px !important;
                white-space: normal !important;
                word-break: break-word !important;
                overflow-wrap: break-word !important;
              }
              .signature-block {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .signature-block * {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .card {
                margin: 0;
                border: none;
                box-shadow: none;
              }
              .card-content {
                padding: 0 !important;
              }
              /* Ensure parent containers allow page breaks */
              .pdf-content {
                page-break-inside: auto !important;
              }
              .card-content {
                page-break-inside: auto !important;
              }
              /* Force page break before Additional Terms section - multiple methods for compatibility */
              .additional-terms-wrapper {
                page-break-before: always !important;
                break-before: page !important;
                display: block !important;
                clear: both !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
                position: relative !important;
              }
              .additional-terms-section {
                page-break-inside: auto !important;
                page-break-before: auto !important;
              }
              /* Add a page break marker before Additional Terms */
              .additional-terms-wrapper::before {
                content: "" !important;
                display: block !important;
                page-break-before: always !important;
                break-before: page !important;
                height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                visibility: hidden !important;
              }
              /* Force page break on the element before Additional Terms */
              .pdf-content > div:has(+ .additional-terms-wrapper) {
                page-break-after: always !important;
                break-after: page !important;
              }
              /* Alternative selector for browsers that don't support :has() */
              .pdf-content > div:last-of-type:not(.additional-terms-wrapper):not(.footer-section) {
                page-break-after: always !important;
                break-after: page !important;
              }
              /* Ensure all elements allow page breaks */
              .pdf-content > * {
                page-break-inside: auto !important;
              }
              /* Page break element before Additional Terms */
              .page-break-before-additional-terms {
                display: none !important;
                page-break-after: always !important;
                break-after: page !important;
              }
              @media print {
                .page-break-before-additional-terms {
                  display: block !important;
                  height: 0 !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  page-break-after: always !important;
                  break-after: page !important;
                }
              }
            }
          </style>
        </head>
        <body>
          <div class="purchase-order-pdf">
            <div class="pdf-content">
              ${pdfContent}
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    toast.success('PDF download initiated');
  };

  const handleSendEmail = () => {
    toast.success('Purchase Order sent via email to supplier');
  };

  const handlePrint = () => {
    // Set document title to PO number for PDF filename
    const originalTitle = document.title;
    const poNumber = currentPO.poNumber || purchaseOrder?.poNumber || 'PO';
    document.title = poNumber;
    
    // Restore original title after a delay
    window.print();
    
    // Restore title after print dialog closes
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleAnalyse = async () => {
    if (!currentPO || !currentPO.lineItems || currentPO.lineItems.length === 0) {
      toast.error('No items to analyze');
      return;
    }

    setLoadingAnalysis(true);
    setAnalysisDialogOpen(true);
    setAnalysisData([]);

    try {
      // Extract rawItemIds from line items
      const rawItemIds: number[] = [];
      const itemMap = new Map<number, { itemName: string; category: string; size: string; poRate: number }>();

      for (const item of currentPO.lineItems) {
        // Find the rawItemId from lineItems (which has PoItemDetail)
        const lineItem = lineItems.find(li => {
          const rawItem = li.rawItem || rawItems.find(ri => ri.id.toString() === (item as any).rawItemId?.toString());
          return rawItem?.itemName === item.itemName;
        });

        if (lineItem) {
          const rawItemId = lineItem.rawItemId || (lineItem.rawItem?.id);
          if (rawItemId && !rawItemIds.includes(rawItemId)) {
            rawItemIds.push(rawItemId);
            const rawItem = lineItem.rawItem || rawItems.find(ri => ri.id === rawItemId);
            itemMap.set(rawItemId, {
              itemName: item.itemName,
              category: (item as any).category || rawItem?.itemCategory?.categoryName || '',
              size: (item as any).size || 'N/A',
              poRate: item.purchaseRate
            });
          }
        }
      }

      if (rawItemIds.length === 0) {
        toast.error('No valid raw items found to analyze');
        setLoadingAnalysis(false);
        return;
      }

      // Fetch batch transactions and inventory quantities in parallel
      const [transactionsResponse, consumptionResponse] = await Promise.all([
        purchaseOrderApiService.getLastTransactionsBatch(
          rawItemIds,
          branchFromContext || undefined
        ),
        grnApiService.getConsumptionByRawItemsBatch(
          rawItemIds,
          branchFromContext || undefined
        )
      ]);

      // Create a map of rawItemId to inventory quantity from consumption data
      const inventoryMap = new Map<number, number>();
      if (consumptionResponse.success && consumptionResponse.data) {
        for (const rawItemId of rawItemIds) {
          const consumptionDetails = consumptionResponse.data[rawItemId] || [];
          const totalRemainingQty = consumptionDetails.reduce((sum: number, detail: GRNConsumptionDetail) => sum + (detail.remainingQuantity || 0), 0);
          inventoryMap.set(rawItemId, totalRemainingQty);
        }
      }

      if (transactionsResponse.success && transactionsResponse.data) {
        // Transform the response into analysis data
        const analysis: Array<{
          itemName: string;
          category: string;
          size: string;
          poRate: number;
          inventoryQty: number;
          pastTransactions: LastTransaction[];
        }> = [];

        for (const rawItemId of rawItemIds) {
          const itemInfo = itemMap.get(rawItemId);
          if (itemInfo) {
            const transactions = transactionsResponse.data[rawItemId] || [];
            // Take only the first 5 transactions
            const pastTransactions = transactions.slice(0, 5);
            const inventoryQty = inventoryMap.get(rawItemId) || 0;
            
            analysis.push({
              itemName: itemInfo.itemName,
              category: itemInfo.category,
              size: itemInfo.size,
              poRate: itemInfo.poRate,
              inventoryQty: inventoryQty,
              pastTransactions: pastTransactions
            });
          }
        }

        setAnalysisData(analysis);
      } else {
        toast.error('Failed to fetch transaction analysis');
      }
    } catch (error) {
      // console.error('Error analyzing PO:', error);
      toast.error('Failed to analyze purchase order');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleApprove = async () => {
    if (!purchaseOrder || !id) {
      toast.error('No purchase order to approve');
      return;
    }

    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    try {
      setLoading(true);
      
      // Get the selected branch's contact person
      let signedByUserId: number | null = null;
      if (branchFromContext) {
        try {
          const selectedBranchData = await branchApiService.getBranchById(branchFromContext.toString());
          if (selectedBranchData?.contactPerson) {
            const contactPersonId = selectedBranchData.contactPerson.toString();
            // Check if it's a numeric ID (not a name)
            if (!isNaN(Number(contactPersonId))) {
              signedByUserId = Number(contactPersonId);
            }
          }
        } catch (error) {
          // console.error('Error fetching selected branch for contact person:', error);
          // Continue with null if branch fetch fails
        }
      }
      
      // Update the purchase order status to 'APPROVED'
      const updateData = {
        id: purchaseOrder.id,
        poNumber: purchaseOrder.poNumber,
        purchaseDate: purchaseOrder.purchaseDate,
        expectedDeliveryDate: purchaseOrder.expectedDeliveryDate,
        actualDeliveryDate: purchaseOrder.actualDeliveryDate,
        paymentTerms: purchaseOrder.paymentTerms,
        deliveryTerms: purchaseOrder.deliveryTerms,
        currency: purchaseOrder.currency,
        exchangeRate: purchaseOrder.exchangeRate,
        status: 'APPROVED',
        effectiveStartDate: purchaseOrder.effectiveStartDate,
        effectiveEndDate: purchaseOrder.effectiveEndDate,
        modeOfDespatch: purchaseOrder.modeOfDespatch,
        transportationCharges: purchaseOrder.transportationCharges,
        insuranceCharges: purchaseOrder.insuranceCharges,
        packingCharges: purchaseOrder.packingCharges,
        installAndServiceCharges: purchaseOrder.installAndServiceCharges,
        projectLeadTime: purchaseOrder.projectLeadTime,
        itemCostOthCharges: purchaseOrder.itemCostOthCharges,
        grandTotalTax: purchaseOrder.grandTotalTax,
        totalAmount: purchaseOrder.totalAmount,
        grandTotal: purchaseOrder.grandTotal,
        amendmentRevisionNumber: purchaseOrder.amendmentRevisionNumber,
        amendmentChangeDate: purchaseOrder.amendmentChangeDate,
        amendmentReason: purchaseOrder.amendmentReason,
        approvedDate: new Date().toISOString().split('T')[0],
        approvedBy: user.id,
        signedBy: signedByUserId || purchaseOrder.signedBy || 0,
        poType: purchaseOrder.poType,
        priority: purchaseOrder.priority,
        sourceType: purchaseOrder.sourceType,
        vendorQuoteNo: purchaseOrder.vendorQuoteNo,
        vendorQuoteDate: purchaseOrder.vendorQuoteDate,
        isActive: purchaseOrder.isActive,
        branchId: purchaseOrder.branchId,
        shipToBranchId: purchaseOrder.shipToBranchId,
        createdBy: purchaseOrder.createdBy,
        updatedBy: user.id,
        supplierId: purchaseOrder.supplier?.id
      };

      await purchaseOrderApiService.updatePurchaseOrder(id, updateData);
      
      // Update local state
      const updatedPurchaseOrder = { ...purchaseOrder, status: 'APPROVED', approvedDate: updateData.approvedDate, approvedBy: updateData.approvedBy };
      setPurchaseOrder(updatedPurchaseOrder);
      setCurrentPO(prev => ({ ...prev, status: 'APPROVED', approvedBy: user.id.toString(), approvedDate: updateData.approvedDate }));
      
      // Set the approved user info since we just approved it
      setApprovedByUser(user);
      // Set the signed by user as the same user for now (can be changed later if needed)
      setSignedByUser(user);
      
      toast.success('Purchase Order approved successfully!');
    } catch (error) {
      // console.error('Error approving purchase order:', error);
      toast.error('Failed to approve purchase order');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading purchase order...</span>
      </div>
    );
  }

  if (error || !purchaseOrder) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Purchase Order</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Purchase Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl purchase-order-pdf">
      {/* Header Actions - Hidden when printing */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Purchase Orders
        </Button>
        <div className="flex gap-2">
          {/* Analyse Button */}
          <Button 
            onClick={handleAnalyse} 
            variant="outline"
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg border-0"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Analyse
          </Button>
          {/* Approve Button - Only show if status is not already approved and not closed */}
          {!isApproved(currentPO.status) && currentPO.status?.toUpperCase() !== 'CLOSED' && (
            <PermissionGate route="/purchase-orders" action="approve">
              <Button 
                onClick={handleApprove} 
                disabled={loading}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {loading ? 'Approving...' : 'Approve'}
              </Button>
            </PermissionGate>
          )}
          <Button 
            onClick={handlePrint} 
            variant="outline"
            disabled={!isApproved(currentPO.status) && !purchaseOrder?.signedBy}
            title={!isApproved(currentPO.status) && !purchaseOrder?.signedBy ? 'Only approved purchase orders or orders with a signature can be printed' : 'Print purchase order'}
          >
            <FileText className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* PDF-Style Purchase Order */}
      <Card className="border-primary-200 shadow-2xl bg-white print:shadow-none print:border-none">
        <CardContent className="p-0">
          {/* PDF Document */}
          <div className="bg-white text-black p-8 print:p-2 pdf-content" style={{ fontFamily: 'Arial, sans-serif', fontSize: '14pt', lineHeight: '1.5' }}>
            
            {/* Header */}
            <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-primary-200 print:mb-2 print:pb-1">
              <div>
                <h1 className="text-2xl font-bold text-primary-800 mb-1">
                  {currentPO.companyDetails.name}
                </h1>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3 w-3" />
                    {currentPO.companyDetails.address.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {currentPO.companyDetails.phone}
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {currentPO.companyDetails.email}
                    </div>
                  </div>
                  <div>GSTIN: {currentPO.companyDetails.gstin}</div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-3xl font-bold text-primary-800 mb-2">PURCHASE ORDER</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 justify-end">
                    <Hash className="h-4 w-4" />
                    <span className="font-medium">PO Number:</span>
                    <span className="font-bold text-primary-700">{currentPO.poNumber}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">PO Date:</span>
                    <span>{formatDate(currentPO.purchaseDate)}</span>
                  </div>
                  <div className="flex justify-end">
                    <Badge className={`${getStatusColor(currentPO.status)} flex items-center gap-1`}>
                      {getStatusIcon(currentPO.status)}
                      {currentPO.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 print:grid-cols-2 print:gap-2 print:mb-2">
              {/* Supplier Information */}
              <div className="bg-primary-50 p-6 rounded-lg print:p-2 print:rounded">
                <div className="flex items-center gap-2 mb-4 print:mb-1">
                  <Building className="h-5 w-5 text-primary-600 print:h-3 print:w-3" />
                  <h3 className="font-bold text-lg text-gray-800 print:text-sm">Supplier Details</h3>
                </div>
                <div className="space-y-2 text-sm print:space-y-1 print:text-xs">
                  <div className="font-bold text-primary-700">{currentPO.supplierDetails.name}</div>
                  <div className="font-medium">Code: {currentPO.supplierDetails.code}</div>
                  <div className="whitespace-pre-line">{currentPO.supplierDetails.address}</div>
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {currentPO.supplierDetails.phone}
                  </div>
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {currentPO.supplierDetails.email}
                  </div>
                  <div>GSTIN: {currentPO.supplierDetails.gstin}</div>
                </div>
              </div>

              {/* Order Information */}
              <div className="bg-primary-50 p-6 rounded-lg print:p-2 print:rounded">
                <div className="flex items-center gap-2 mb-4 print:mb-1">
                  <FileText className="h-5 w-5 text-primary-600 print:h-3 print:w-3" />
                  <h3 className="font-bold text-lg text-gray-800 print:text-sm">Order Information</h3>
                </div>
                <div className="space-y-3 text-sm print:space-y-1 print:text-xs">
                  <div className="grid grid-cols-2 gap-4 print:gap-2">
                    <div>
                      <span className="text-gray-600 print:text-xs">PO Type:</span>
                      <div className="font-medium print:text-xs">{currentPO.poType}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 print:text-xs">Ship To:</span>
                      <div className="font-medium print:text-xs">{currentPO.shipTo}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 print:gap-2">
                    <div>
                      <span className="text-gray-600 print:text-xs">Start Date:</span>
                      <div className="font-medium print:text-xs">{formatDate(currentPO.effectiveStartDate)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 print:text-xs">End Date:</span>
                      <div className="font-medium print:text-xs">{formatDate(currentPO.effectiveEndDate)}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 print:gap-2">
                    <div>
                      <span className="text-gray-600 print:text-xs">Lead Time:</span>
                      <div className="font-medium print:text-xs">{currentPO.projectLeadTime}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 print:text-xs">Payment Terms:</span>
                      <div className="font-medium print:text-xs">{currentPO.paymentTerms} days</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 print:gap-2">
                    <div>
                      <span className="text-gray-600 print:text-xs">Vendor Quote No:</span>
                      <div className="font-medium print:text-xs">{currentPO.vendorCodeNumber || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600 print:text-xs">Vendor Quote Date:</span>
                      <div className="font-medium print:text-xs">{currentPO.vendorCodeDate ? formatDate(currentPO.vendorCodeDate) : 'N/A'}</div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-1 text-gray-600 mb-1 print:text-xs">
                      <Truck className="h-3 w-3 print:h-2 print:w-2" />
                      Mode of Dispatch:
                    </div>
                    <div className="font-medium print:text-xs">{currentPO.modeOfDispatch}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="mb-0 print:mb-0 print:-mx-2">
              <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2 print:text-sm print:mb-2">
                <Package className="h-5 w-5 text-primary-600 print:h-4 print:w-4" />
                Order Items
              </h3>
              <div className="border border-gray-300 rounded-lg overflow-hidden print:overflow-visible print:border-gray-400 table-wrapper">
                <Table className="table print:text-[9px]">
                  <TableHeader>
                    <TableRow className="bg-primary-100">
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 print:text-[9px] print:py-1 print:px-1">S.No</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 print:text-[9px] print:py-1 print:px-1 item-description max-w-[300px] whitespace-normal break-words">
                        Item Description
                      </TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 print:text-[9px] print:py-1 print:px-1">HSN</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 text-center print:text-[9px] print:py-1 print:px-1">Qty</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 print:text-[9px] print:py-1 print:px-1">Unit</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 print:text-[9px] print:py-1 print:px-1">Grade</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 text-right print:text-[9px] print:py-1 print:px-1">Rate ({getCurrencySymbol()})</TableHead>
                      <TableHead className={`font-bold text-gray-800 border-r border-gray-300 text-center print:text-[9px] print:py-1 print:px-1 ${shouldHideTaxColumns.hideIGST ? 'print:hidden' : ''}`}>IGST</TableHead>
                      <TableHead className={`font-bold text-gray-800 border-r border-gray-300 text-center print:text-[9px] print:py-1 print:px-1 ${shouldHideTaxColumns.hideCGST ? 'print:hidden' : ''}`}>CGST</TableHead>
                      <TableHead className={`font-bold text-gray-800 border-r border-gray-300 text-center print:text-[9px] print:py-1 print:px-1 ${shouldHideTaxColumns.hideSGST ? 'print:hidden' : ''}`}>SGST</TableHead>
                      <TableHead className="font-bold text-gray-800 border-r border-gray-300 text-right print:text-[9px] print:py-1 print:px-1">Tax Amount ({getCurrencySymbol()})</TableHead>
                      <TableHead className="font-bold text-gray-800 text-right print:text-[9px] print:py-1 print:px-1">Amount ({getCurrencySymbol()})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentPO.lineItems.map((item, index) => (
                      <TableRow key={item.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} print:text-[9px]`}>
                        <TableCell className="border-r border-gray-200 font-medium print:py-1 print:px-1">{index + 1}</TableCell>
                        <TableCell className="border-r border-gray-200 print:py-1 print:px-1 item-description max-w-[300px] whitespace-normal break-words align-top">
                          <div className="font-medium print:text-[9px] whitespace-normal break-words">{item.itemName}</div>
                          {((item as any).size && (item as any).size !== 'N/A' && (item as any).size !== '-' && (item as any).size.trim() !== '') && (
                            <div className="text-xs text-gray-600 mt-1 print:text-[9px]">Size: {(item as any).size}</div>
                          )}
                        </TableCell>
                        <TableCell className="border-r border-gray-200 font-mono text-sm print:text-[9px] print:py-1 print:px-1">{item.hsn}</TableCell>
                        <TableCell className="border-r border-gray-200 text-center font-medium print:py-1 print:px-1">{item.quantity.toLocaleString()}</TableCell>
                        <TableCell className="border-r border-gray-200 print:py-1 print:px-1">{item.unit}</TableCell>
                        <TableCell className="border-r border-gray-200 print:py-1 print:px-1">{item.grade}</TableCell>
                        <TableCell className="border-r border-gray-200 text-right font-medium print:py-1 print:px-1">{formatCurrency(item.purchaseRate)}</TableCell>
                        {(() => {
                          const taxPercent = (item as any).igst ?? (item as any).taxPercent ?? (item as any).gstPercent ?? 0;
                          const interstate = isInterStateSupply();
                          const igstPct = interstate ? taxPercent : 0;
                          const cgstPct = interstate ? 0 : taxPercent / 2;
                          const sgstPct = interstate ? 0 : taxPercent / 2;
                          return (
                            <>
                              <TableCell className={`border-r border-gray-200 text-center print:py-1 print:px-1 ${shouldHideTaxColumns.hideIGST ? 'print:hidden' : ''}`}>{igstPct ? `${igstPct}%` : 'NA'}</TableCell>
                              <TableCell className={`border-r border-gray-200 text-center print:py-1 print:px-1 ${shouldHideTaxColumns.hideCGST ? 'print:hidden' : ''}`}>{cgstPct ? `${cgstPct}%` : 'NA'}</TableCell>
                              <TableCell className={`border-r border-gray-200 text-center print:py-1 print:px-1 ${shouldHideTaxColumns.hideSGST ? 'print:hidden' : ''}`}>{sgstPct ? `${sgstPct}%` : 'NA'}</TableCell>
                            </>
                          );
                        })()}
                        <TableCell className="border-r border-gray-200 text-right print:py-1 print:px-1">{formatCurrency(item.totalTax)}</TableCell>
                        <TableCell className="text-right font-bold text-primary-700 print:py-1 print:px-1">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Charges and Totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 print:grid-cols-2 print:gap-4 print:mb-4">
              {/* Additional Charges */}
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-4">Additional Charges</h3>
                <div className="bg-primary-50 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Transportation Charges:</span>
                    <span className="font-medium">{formatCurrency(currentPO.transportationCharges)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Insurance Charges:</span>
                    <span className="font-medium">{formatCurrency(currentPO.insuranceCharges)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Packing Charges:</span>
                    <span className="font-medium">{formatCurrency(currentPO.packingCharges)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Installation Service:</span>
                    <span className="font-medium">{formatCurrency(currentPO.installationServiceCharges)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Other Charges:</span>
                    <span className="font-medium">{formatCurrency(currentPO.otherCharges)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold">
                    <span>Total Additional Charges:</span>
                    <span className="text-primary-700">
                      {formatCurrency(
                        currentPO.transportationCharges +
                        currentPO.insuranceCharges +
                        currentPO.packingCharges +
                        currentPO.installationServiceCharges +
                        currentPO.otherCharges
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-4">Order Summary</h3>
                <div className="bg-primary-50 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatCurrency(currentPO.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Total Tax (GST):</span>
                    <span className="font-medium">{formatCurrency(currentPO.grandTotalTax)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Additional Charges:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        currentPO.transportationCharges +
                        currentPO.insuranceCharges +
                        currentPO.packingCharges +
                        currentPO.installationServiceCharges +
                        currentPO.otherCharges
                      )}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-xl font-bold">
                    <span>Grand Total:</span>
                    <span className="text-primary-700">{formatCurrency(currentPO.grandTotal)}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <div className="text-sm font-semibold text-gray-700 mb-1">Amount in Words:</div>
                    <div className="text-sm text-gray-800 italic">
                      {numberToWords(currentPO.grandTotal).charAt(0).toUpperCase() + numberToWords(currentPO.grandTotal).slice(1)} Only
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms + Digital Signature */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 print:grid-cols-2 print:gap-4">
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary-600" />
                  Terms & Conditions
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-600 font-semibold leading-relaxed whitespace-pre-line">
                    {currentPO.termConditions}
                  </p>
                </div>
              </div>

              <div className="pt-6 border-t-2 border-gray-300 signature-block md:pt-0 md:border-t-0 md:border-l-2 md:border-gray-300 md:pl-6 print:pt-0 print:border-t-0 print:border-l-2 print:border-gray-300 print:pl-6">
                {isApprovedOrClosed(currentPO.status) ? (
                  <div className="text-center">
                    <div className="text-xs text-gray-400 mb-2">Digital Signature</div>
                    <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                      <div className="text-xs text-gray-600 font-mono">
                        {(() => {
                          if (signedByUser && typeof signedByUser === 'object' && 'firstName' in signedByUser) {
                            const firstName = signedByUser.firstName || '';
                            const lastName = signedByUser.lastName || '';
                            const name = `${firstName} ${lastName}`.trim();
                            if (name) return name;
                          }

                          if (approvedByUser && typeof approvedByUser === 'object' && 'firstName' in approvedByUser) {
                            const firstName = approvedByUser.firstName || '';
                            const lastName = approvedByUser.lastName || '';
                            const name = `${firstName} ${lastName}`.trim();
                            if (name) return name;
                          }

                          if (contactPersonUser && typeof contactPersonUser === 'object' && 'firstName' in contactPersonUser) {
                            const firstName = contactPersonUser.firstName || '';
                            const lastName = contactPersonUser.lastName || '';
                            const name = `${firstName} ${lastName}`.trim();
                            if (name) return name;
                          }

                          if (branchInfo?.contactPerson) {
                            const contactPerson = branchInfo.contactPerson.toString();
                            if (isNaN(Number(contactPerson))) return contactPerson;
                          }

                          return 'N/A';
                        })()}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Signed on: {formatDate(currentPO.approvedDate)}
                      </div>
                    </div>

                    {isApprovedOrClosed(currentPO.status) && purchaseOrder?.signedBy && (
                      <div className="border-t border-gray-400 pt-2 mt-8 mx-8">
                        <div className="font-bold text-gray-800">Signed By</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="border-t border-gray-400 pt-2 mt-8 mx-8">
                      <div className="font-bold text-gray-800">Pending Approval</div>
                      <div className="text-sm text-gray-600">This purchase order is awaiting approval</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Static Terms */}
            {staticTerms && (
              <>
                <div 
                  className="page-break-before-additional-terms"
                  style={{ 
                    pageBreakAfter: 'always', 
                    breakAfter: 'page'
                  }}
                ></div>
                <div className="additional-terms-wrapper" style={{ pageBreakBefore: 'always', breakBefore: 'page' }}>
                <div className="mt-4 pt-4 additional-terms-section">
                  <h3 className="font-bold text-lg text-gray-800 mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary-600" />
                    Additional Terms & Conditions
                  </h3>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-700 leading-relaxed">
                      {Array.isArray(staticTerms) ? (
                        <ol className="list-decimal list-inside space-y-2">
                          {staticTerms.map((term, index) => (
                            <li key={index} className="pl-2">
                              {term}
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <div className="whitespace-pre-line">
                          {staticTerms}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 mt-8 pt-4 border-t border-gray-300">
              This is a computer generated purchase order and does not require physical signature.
              {currentPO.companyDetails.email && currentPO.companyDetails.phone && (
                <>
                  <br />
                  For any queries, please contact us at {currentPO.companyDetails.email} or {currentPO.companyDetails.phone}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {/* <div className="flex justify-center gap-4 print:hidden">
        <Button
          onClick={handlePrint}
          variant="outline"
          className="border-primary-200 text-primary-700 hover:bg-primary-50"
        >
          <FileText className="h-4 w-4 mr-2" />
          Print
        </Button>
        
        <Button
          onClick={handleDownloadPDF}
          className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
        >
          <Download className="h-4 w-4 mr-2" />
          Download as PDF
        </Button>
        
        <Button
          onClick={handleSendEmail}
          variant="outline"
          className="border-blue-200 text-blue-700 hover:bg-blue-50"
        >
          <Mail className="h-4 w-4 mr-2" />
          Send via Email
        </Button>
      </div> */}

      {/* Analysis Dialog */}
      <Dialog open={analysisDialogOpen} onOpenChange={setAnalysisDialogOpen}>
        <DialogContent className="!max-w-[98vw] !w-[98vw] max-h-[90vh] p-0 flex flex-col overflow-hidden [&>button]:fixed [&>button]:z-[70]">
          <div className="flex-shrink-0 px-4 pt-6 pb-4 border-b border-gray-200 relative">
            <DialogHeader>
              <DialogTitle>Analyse the Purchase Order with {currentPO?.supplierDetails?.name || currentPO?.supplier || 'Supplier'}</DialogTitle>
              <DialogDescription>
                Historical transaction analysis for all items in this purchase order
              </DialogDescription>
            </DialogHeader>
          </div>
          
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {loadingAnalysis ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Analyzing purchase order...</span>
              </div>
            ) : analysisData.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">No analysis data available</div>
              </div>
            ) : (
              <div className="w-full min-w-0 overflow-x-auto">
                <Table className="w-full" style={{ tableLayout: 'auto', width: '100%' }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold" style={{ width: '18%', minWidth: '150px' }}>Item Name</TableHead>
                      <TableHead className="font-semibold text-right" style={{ width: '8%', minWidth: '80px' }}>PO Rate</TableHead>
                      <TableHead className="font-semibold text-right" style={{ width: '8%', minWidth: '100px' }}>Inventory Qty</TableHead>
                      <TableHead className="font-semibold" style={{ width: '14.5%', minWidth: '150px' }}>Past 1</TableHead>
                      <TableHead className="font-semibold" style={{ width: '14.5%', minWidth: '150px' }}>Past 2</TableHead>
                      <TableHead className="font-semibold" style={{ width: '14.5%', minWidth: '150px' }}>Past 3</TableHead>
                      <TableHead className="font-semibold" style={{ width: '14.5%', minWidth: '150px' }}>Past 4</TableHead>
                      <TableHead className="font-semibold" style={{ width: '14.5%', minWidth: '150px' }}>Past 5</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysisData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="align-top break-words">
                          <div className="min-w-0">
                            <div className="font-medium break-words">Name: {item.itemName}</div>
                            {item.category && (
                              <div className="text-sm text-gray-500 mt-1 break-words">Category: {item.category}</div>
                            )}
                            <div className="text-sm text-gray-500 mt-1">Size: {item.size || 'N/A'}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium align-top">
                          {item.poRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right align-top">
                          {item.inventoryQty > 0 ? item.inventoryQty.toLocaleString() : ''}
                        </TableCell>
                        {[0, 1, 2, 3, 4].map((idx) => {
                          const transaction = item.pastTransactions[idx];
                          return (
                            <TableCell key={idx} className="align-top" style={{ 
                              wordWrap: 'break-word', 
                              overflowWrap: 'break-word', 
                              minWidth: '150px',
                              maxWidth: '150px',
                              whiteSpace: 'normal',
                              padding: '8px'
                            }}>
                              {transaction ? (
                                <div className="text-sm" style={{ 
                                  minWidth: 0, 
                                  wordWrap: 'break-word', 
                                  overflowWrap: 'break-word',
                                  whiteSpace: 'normal'
                                }}>
                                  <div className="font-medium" style={{ 
                                    wordWrap: 'break-word', 
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.4'
                                  }}>{transaction.supplierName}</div>
                                  <div className="text-gray-600 mt-1">Rate: {transaction.rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  <div className="text-gray-500 text-xs mt-1" style={{ 
                                    wordWrap: 'break-word', 
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'normal'
                                  }}>
                                    Purchase Date: {transaction.poDate ? new Date(transaction.poDate).toLocaleDateString('en-GB') : '-'}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-gray-400 text-sm">Not available</div>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}